import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { IntakeFormDefinition, IntakeFormFileValue, IntakeFormValues } from '@aline/common';
import {
  IntakeFormDefinitionSchema,
  IntakeFormValuesSchema,
  sanitizeIntakeFormValues,
  validateIntakeFormValues,
} from '@aline/common';
import { Skeleton, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { draftFieldsFromDefinition } from 'src/components/workflow/workflow-detail/intake-form/intake-form-fields';
import { IntakeFormFields } from 'src/components/workflow/workflow-detail/intake-form/IntakeFormFields';
import { Apollo } from 'src/gql/generated';
import { getTypographyForSx } from 'src/theme';
import { focusFieldControl } from 'src/utils/focus-field-control';
import { makeStyles } from 'tss-react/mui';

import {
  CardPager,
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './AskUser';

const SKELETON_LABEL_WIDTHS = ['44%', '56%', '38%'] as const;

const useStyles = makeStyles()(theme => ({
  error: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.palette.error.main,
    fontSize: 12,
    marginTop: theme.spacing(0.5),
  },
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    /**
     * Keep the chat HITL card from growing a stray horizontal scrollbar when
     * intake controls (file dropzone, selects) sit at full width.
     */
    minWidth: 0,
    overflowX: 'hidden',
    width: '100%',
  },
  formSubtitle: {
    ...getTypographyForSx(theme.typography.subtitle2),
    marginBottom: theme.spacing(1),
  },
  skeletonField: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
}));

interface WorkflowCollectIntakeFormCardProps {
  messageId: string;
  suggestedValues: IntakeFormValues;
  templateWorkflowId: string;
  toolCallId: string;
  maxHeight?: number | null;
}

/**
 * Fetches the template intake form, then mounts a keyed ready card so values
 * seed once via useState (no effect-driven reset when the query resolves).
 * Kept for historical `workflowCollectIntakeForm` tool calls only.
 */
export function WorkflowCollectIntakeFormCard({
  maxHeight,
  messageId,
  suggestedValues,
  templateWorkflowId,
  toolCallId,
}: WorkflowCollectIntakeFormCardProps) {
  const { data, error, loading } = Apollo.useWorkflowIntakeFormQuery({
    variables: { workflowId: templateWorkflowId },
  });
  const template = data?.workflow?.intakeFormTemplate ?? null;
  const parsedDefinition = useMemo(
    () => IntakeFormDefinitionSchema.safeParse(template?.formDefinition),
    [template?.formDefinition]
  );
  const parsedDefaults = useMemo(
    () => IntakeFormValuesSchema.safeParse(template?.defaultValues),
    [template?.defaultValues]
  );

  if (loading)
    return (
      <PendingInteractionCardChrome expanded maxHeight={maxHeight} title="Intake Questions">
        <CollectIntakeFormLoading />
      </PendingInteractionCardChrome>
    );

  if (error || !template || !parsedDefinition.success) {
    return (
      <PendingInteractionCardChrome expanded maxHeight={maxHeight} title="Intake Questions">
        <CollectIntakeFormError message="The workflow intake form could not be loaded. Please try again." />
      </PendingInteractionCardChrome>
    );
  }

  return (
    <ReadyWorkflowCollectIntakeFormCard
      key={toolCallId}
      definition={parsedDefinition.data}
      formTitle={template.title?.trim() || null}
      initialValues={getInitialIntakeFormValues(
        parsedDefinition.data,
        parsedDefaults,
        suggestedValues
      )}
      maxHeight={maxHeight}
      messageId={messageId}
      toolCallId={toolCallId}
    />
  );
}

function ReadyWorkflowCollectIntakeFormCard({
  definition,
  formTitle,
  initialValues,
  maxHeight,
  messageId,
  toolCallId,
}: {
  definition: IntakeFormDefinition;
  formTitle: string | null;
  initialValues: IntakeFormValues;
  messageId: string;
  toolCallId: string;
  maxHeight?: number | null;
}) {
  const { classes } = useStyles();
  const { addToolOutput } = useChatAdapter();
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState(initialValues);
  const formFieldsRef = useRef<HTMLDivElement | null>(null);
  const [deleteDocuments] = Apollo.useDeleteDocumentsMutation();
  const fields = useMemo(() => draftFieldsFromDefinition(definition), [definition]);
  const total = fields.length;
  const validation = validateIntakeFormValues(definition, values);
  const submitDisabledReason = getSubmitDisabledReason({ submitting, validation });

  const removeFile = useCallback(
    async (file: IntakeFormFileValue) => {
      await deleteDocuments({ variables: { input: { documentIds: [file.documentId] } } });
    },
    [deleteDocuments]
  );

  const focusField = (index: number) => {
    if (total === 0) return;
    const next = ((index % total) + total) % total;
    setFocusedIndex(next);
    const field = getIntakeFieldElement(formFieldsRef.current, next);
    if (!field) return;
    field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    focusFieldControl(field);
  };

  const submit = async () => {
    if (submitDisabledReason) return;
    try {
      setSubmitting(true);
      await addToolOutput({
        messageId,
        output: { intakeFormValues: validation.sanitizedValues },
        tool: 'workflowCollectIntakeForm',
        toolCallId,
      });
    } catch (submitError) {
      Sentry.captureException(submitError);
      enqueueSnackbar('Failed to submit the form. Please try again.', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      await addToolOutput({
        messageId,
        output: { skipped: true },
        tool: 'workflowCollectIntakeForm',
        toolCallId,
      });
    } catch (skipError) {
      Sentry.captureException(skipError);
      enqueueSnackbar('Failed to skip the form. Please try again.', {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PendingInteractionCardChrome
      bodyRef={formFieldsRef}
      expanded={expanded}
      footer={
        <>
          <CardSecondaryAction disabled={submitting} onClick={() => void skip()}>
            Skip
          </CardSecondaryAction>
          <CardPrimaryAction
            disabled={submitting}
            disabledReason={submitDisabledReason}
            loading={submitting}
            onClick={() => void submit()}
          >
            Submit
          </CardPrimaryAction>
        </>
      }
      headerActions={
        expanded &&
        total > 1 && <CardPager index={focusedIndex} total={total} onMove={focusField} />
      }
      maxHeight={maxHeight}
      title="Intake Questions"
      toggleLabel="intake form"
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <div
        className={classes.formFields}
        onFocusCapture={event => {
          const index = getFocusedIntakeFieldIndex(formFieldsRef.current, event.target);
          if (index !== null) setFocusedIndex(index);
        }}
      >
        <Typography className={classes.formSubtitle}>{formTitle || 'Untitled Form'}</Typography>
        <IntakeFormFields
          fields={fields}
          formDefinition={definition}
          values={values}
          onChange={(name, value) => setValues(current => ({ ...current, [name]: value }))}
          onRemoveFile={removeFile}
        />
      </div>
    </PendingInteractionCardChrome>
  );
}

function CollectIntakeFormLoading() {
  const { classes } = useStyles();
  return (
    <div aria-busy="true" aria-label="Loading intake form" className={classes.formFields}>
      {SKELETON_LABEL_WIDTHS.map((width, index) => (
        <div key={index} className={classes.skeletonField}>
          <Skeleton height={14} variant="text" width={width} />
          <Skeleton height={36} variant="rounded" width="100%" />
        </div>
      ))}
    </div>
  );
}

function CollectIntakeFormError({ message }: { message: string }) {
  const { classes } = useStyles();
  return <Typography className={classes.error}>{message}</Typography>;
}

function getIntakeFieldElement(container: HTMLElement | null, index: number) {
  if (!container) return null;
  return container.querySelector<HTMLElement>(`[data-intake-field="${index}"]`);
}

function getFocusedIntakeFieldIndex(container: HTMLElement | null, target: EventTarget) {
  if (!container || !(target instanceof Node)) return null;
  const fields = container.querySelectorAll<HTMLElement>('[data-intake-field]');
  for (const [index, field] of fields.entries()) {
    if (field.contains(target)) return index;
  }
  return null;
}

function getSubmitDisabledReason({
  submitting,
  validation,
}: {
  submitting: boolean;
  validation: ReturnType<typeof validateIntakeFormValues>;
}): string {
  if (submitting) return 'Submitting...';
  if (validation.missingRequiredFields.length > 0)
    return 'Answer required questions before submitting';
  if (validation.fieldErrors && Object.keys(validation.fieldErrors).length > 0)
    return 'Fix invalid fields before submitting';
  return '';
}

export function getInitialIntakeFormValues(
  definition: Parameters<typeof sanitizeIntakeFormValues>[0],
  parsedDefaults: ReturnType<typeof IntakeFormValuesSchema.safeParse>,
  suggestedValues: IntakeFormValues
) {
  const defaults = sanitizeIntakeFormValues(
    definition,
    parsedDefaults.success ? parsedDefaults.data : {}
  );
  const suggestions = sanitizeIntakeFormValues(definition, suggestedValues);
  return { ...defaults, ...suggestions };
}
